import mapboxgl from 'mapbox-gl';
import type { SectionVertex } from './types';

/** Feet per metre, for turning Mapbox's metres into section distances. */
const FEET_PER_METRE = 3.28084;

/**
 * The distance along a polyline of each of its vertices, in feet.
 *
 * @param {Array} vertices - The line's vertices, in order.
 * @return {Array} The cumulative distance at each vertex, starting at zero.
 */
const cumulativeDistances = (vertices: SectionVertex[]): number[] => {
  const distances = [0];
  for (let index = 1; index < vertices.length; index += 1) {
    const from = new mapboxgl.LngLat(
      vertices[index - 1].lon,
      vertices[index - 1].lat
    );
    const to = new mapboxgl.LngLat(vertices[index].lon, vertices[index].lat);
    distances.push(distances[index - 1] + from.distanceTo(to) * FEET_PER_METRE);
  }
  return distances;
};

/**
 * The point on the section at a given distance along it.
 *
 * The client is not the authority on the section's geometry — the backend measures
 * the line and every depth is placed against its distances. This is only for tying
 * the two views together: it answers "where on the map is this point of the profile",
 * which needs no more precision than a marker.
 *
 * @param {Array} vertices - The section's vertices, in order.
 * @param {number} distanceFt - How far along the section to go.
 * @return {SectionVertex | null} The point, or null when the line is too short.
 */
export const pointAtDistance = (
  vertices: SectionVertex[],
  distanceFt: number
): SectionVertex | null => {
  if (vertices.length < 2) {
    return null;
  }
  const distances = cumulativeDistances(vertices);
  const total = distances[distances.length - 1];
  if (total === 0) {
    return vertices[0];
  }
  const target = Math.min(Math.max(distanceFt, 0), total);

  for (let index = 1; index < vertices.length; index += 1) {
    if (target <= distances[index]) {
      const span = distances[index] - distances[index - 1];
      const fraction = span === 0 ? 0 : (target - distances[index - 1]) / span;
      const from = vertices[index - 1];
      const to = vertices[index];
      return {
        lat: from.lat + (to.lat - from.lat) * fraction,
        lon: from.lon + (to.lon - from.lon) * fraction,
      };
    }
  }
  return vertices[vertices.length - 1];
};

/**
 * How far along the section a map point falls, and how far off the line it is.
 *
 * The perpendicular distance is what decides whether a hover near the line counts as
 * pointing at it: a hover in open map is not a position on the section.
 *
 * @param {Array} vertices - The section's vertices, in order.
 * @param {SectionVertex} at - The point hovered on the map.
 * @return {object | null} The distance along and the offset, or null with no line.
 */
export const distanceAlongSection = (
  vertices: SectionVertex[],
  at: SectionVertex
): { distanceFt: number; offsetFt: number } | null => {
  if (vertices.length < 2) {
    return null;
  }
  const distances = cumulativeDistances(vertices);
  const point = new mapboxgl.LngLat(at.lon, at.lat);

  let best: { distanceFt: number; offsetFt: number } | null = null;

  for (let index = 1; index < vertices.length; index += 1) {
    const from = vertices[index - 1];
    const to = vertices[index];
    // Projected in degrees, scaled for latitude, which is close enough over a
    // segment to place a marker.
    const scale = Math.cos((from.lat * Math.PI) / 180);
    const dx = (to.lon - from.lon) * scale;
    const dy = to.lat - from.lat;
    const lengthSq = dx * dx + dy * dy;
    const fraction =
      lengthSq === 0
        ? 0
        : Math.min(
            Math.max(
              ((at.lon - from.lon) * scale * dx + (at.lat - from.lat) * dy) /
                lengthSq,
              0
            ),
            1
          );
    const nearest = new mapboxgl.LngLat(
      from.lon + (to.lon - from.lon) * fraction,
      from.lat + (to.lat - from.lat) * fraction
    );
    const offsetFt = point.distanceTo(nearest) * FEET_PER_METRE;
    if (best === null || offsetFt < best.offsetFt) {
      const span = distances[index] - distances[index - 1];
      best = {
        distanceFt: distances[index - 1] + span * fraction,
        offsetFt,
      };
    }
  }

  return best;
};
